import { useState, useEffect } from 'react'
import {
  Flex,
  Spinner,
  Text,
  View,
} from '@instructure/ui'

export const ResultsDisplay = ({
  setResultsRef,
  setAwaitingResultsRef,
}) => {
  const [results, setResults] = useState({})
  const [awaitingResults, setAwaitingResults] = useState(false)

  useEffect(() => {
    setResultsRef.current = setResults
  }, [setResultsRef, setResults])

  useEffect(() => {
    setAwaitingResultsRef.current = setAwaitingResults
  }, [setAwaitingResultsRef, setAwaitingResults])

  const { points, show_score, error_grading } = results ?? {}

  if (show_score === false) {
    // District benchmark with immediate results disabled
    return <SimpleMessage>{_t('Your answers have been received.')}</SimpleMessage>
  } else if (error_grading) {
    return <SimpleMessage>{_t('Your answers have been received, but scores are not available right now.')}</SimpleMessage>
  } else if (points != null) {
    return <ResultsReady results={results} />
  } else if (awaitingResults) {
    return <Awaiting />
  } else {
    return null
  }
}

const Awaiting = ({}) => {
  return (
    <Flex direction="column" alignItems="center" margin="medium">
      <Flex.Item>
        <Spinner renderTitle={_t('Wait a moment…')} />
      </Flex.Item>
      <Flex.Item>
        <Text>{_t('Scoring your answers…')}</Text>
      </Flex.Item>
    </Flex>
  )
}

const SimpleMessage = ({ children }) => (
  <View as="div" margin="medium" textAlign="center">
    { children }
  </View>
)

const ResultsReady = ({ results }) => {
  const {
    points,
    points_possible,
    scores,
    simple_scores,
    show_score,
    mastery = {},
    colors,
    mastery_full_names,
    non_auto_graded_items
  } = results

  const nonAutoGradedCount = non_auto_graded_items?.length || 0;

  const percent = Math.round(100 * +points / +points_possible)

  // re-shape scores to look like simple_scores and smash them together,
  // on the off chance the server decided to send both
  const allScores = Object.values(scores || {}).reduce((acc, score) => {
    acc[score.name] = score
    return acc
  }, simple_scores || {})

  const masteryResults = Object.entries(mastery)
    .map(
      ([key, mastery_value]) => ({
        alignment: key,
        name: mastery_full_names?.[key] ?? titleCase(mastery_value),
        color: colors?.[key] ?? '000000',
        pointsString: allScores?.[key]
          ? `${allScores[key].value}/${allScores[key].total_score}`
          : ""
      })
    )

  // This does not use all-InstUI components, since we're not yet redesigning
  // this page. The markup should take advantage of the existing CSS, for now.
  return (
    <Flex direction="column" alignItems="center">
      <Flex.Item>
        <div className="overall-results">
          <span>{`${points}/${points_possible}`}</span>
          <span>{`${percent}%`}</span>
        </div>
        {nonAutoGradedCount > 0 ? (
            <View padding="medium">
              <Text wrap>{`${nonAutoGradedCount} ${_t('manually graded item(s) have been scored at their full point value for the preview.')}`}</Text>
            </View>
           ) : null}
      </Flex.Item>
      <Flex.Item>
        <table className="alignment-mastery-levels">
          {
            masteryResults.map((result) => (
              <MasteryResultRow key={result.alignment} {...result} />
            ))
          }
        </table>
      </Flex.Item>
    </Flex>
  )
}

const MasteryResultRow = ({
  alignment,
  name,
  color,
  pointsString
}) => (
  <tr>
    <td className="mastery-alignment">{alignment}</td>
    <td className="mastery-points">{pointsString}</td>
    <td className="mastery">
      <div className="indicator" style={{ backgroundColor: `#${color}` }} />
      <span>{name}</span>
    </td>
  </tr>
)

// Shamelessly stolen from
// https://medium.freecodecamp.com/three-ways-to-title-case-a-sentence-in-javascript-676a9175eb27#.839l12gmh
const titleCase = (str) =>
  str.replace('_', ' ').toLowerCase().split(' ').map((word) =>
    (word.charAt(0).toUpperCase() + word.slice(1))
  ).join(' ')
